// Shared venue image helpers. Extracted from GuestMapSurface.js to break the
// circular import between GuestMapSurface ↔ EventsCarousel (both needed each
// other's helpers — see code review 2026-06-10).
//
// Consumers:
//   - GuestMapSurface, EventsCarousel, ClosestCarousel — all re-import from here
//   - GuestMapPage — imports the re-export still exposed by GuestMapSurface

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback pool — ONLY used when a venue has zero uploaded photos.
// A venue with uploaded photos will always show its own photos exclusively;
// this pool is a demo/seed safety net so cards never render a blank image.
export const venueImagePool = [
  'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=200&h=200&fit=crop',
  'https://images.unsplash.com/photo-1544025162-d76694265947?w=200&h=200&fit=crop',
  'https://images.unsplash.com/photo-1615141982883-c7ad0e69fd62?w=200&h=200&fit=crop',
  'https://images.unsplash.com/photo-1674216645383-afc4e42c227f?w=200&h=200&fit=crop',
  'https://images.unsplash.com/photo-1739501546796-8e017faf5462?w=200&h=200&fit=crop',
  'https://images.unsplash.com/photo-1528718571269-d0694a3bc668?w=200&h=200&fit=crop',
];

/** Resolve a photo URL — prepends REACT_APP_BACKEND_URL for stored relative
 *  paths (e.g. `/uploads/xxx.jpg`), returns absolute URLs untouched. */
export function resolveVenuePhoto(url) {
  if (!url) return null;
  if (typeof url !== 'string') return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return url;
}

/** Ordered list of every uploaded photo the venue owns, resolved to full URLs.
 *  `main_photo` is placed first (deduped against `photo_urls`). Returns an
 *  empty array when the venue has no uploaded photos. */
export function getVenueOwnPhotos(venue) {
  if (!venue) return [];
  const seen = new Set();
  const out = [];
  const push = (raw) => {
    const resolved = resolveVenuePhoto(raw);
    if (!resolved) return;
    if (seen.has(resolved)) return;
    seen.add(resolved);
    out.push(resolved);
  };
  push(venue.main_photo);
  if (Array.isArray(venue.photo_urls)) {
    venue.photo_urls.forEach(push);
  }
  // Legacy fallback — some seed docs only have `image_url`.
  push(venue.image_url);
  return out;
}

/** Primary hero image for a venue. Prefers uploaded photos, then legacy
 *  `image_url`, then a deterministic slot in the demo pool. */
export function getVenueImage(venue, idx) {
  const own = getVenueOwnPhotos(venue);
  if (own.length > 0) return own[0];
  return venueImagePool[idx % venueImagePool.length];
}
